// src/components/products/ui/FormField.jsx

import React from 'react';
import { Info, AlertCircle } from 'lucide-react';
import Tooltip from './Tooltip';

const FormField = ({ 
  label, 
  required, 
  helperText, 
  tooltipText, 
  children, 
  error 
}) => {
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--spacing-xxs)'
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-xs)',
      fontSize: 'var(--font-size-body4)',
      fontWeight: 'var(--font-weight-semibold)', // Made bold as requested
      color: 'var(--Text-Primary)',
      fontFamily: 'var(--font-family-primary)'
    },
    requiredStar: {
      color: 'var(--Red-500)'
    },
    infoIcon: {
      width: '16px',
      height: '16px',
      color: 'var(--Grey-400)',
      cursor: 'help'
    },
    helperText: {
      fontSize: 'var(--font-size-body5)',
      color: 'var(--Text-Tertiary)',
      marginTop: '2px',
      fontFamily: 'var(--font-family-primary)'
    },
    errorText: {
      fontSize: 'var(--font-size-body5)',
      color: 'var(--Text-Error)',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginTop: '2px',
      fontFamily: 'var(--font-family-primary)'
    },
    errorIcon: {
      width: '12px',
      height: '12px'
    }
  };

  // Add global styles for select elements to fix dropdown arrow padding
  React.useEffect(() => {
    const styleId = 'form-field-select-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        select {
          appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2373737D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right var(--spacing-s) center;
          background-size: 20px;
          padding-right: calc(var(--spacing-s) * 3) !important;
        }
        
        select:focus {
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%232C2C30' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
        }
        
        input:focus, select:focus, textarea:focus {
          outline: none;
          border-color: var(--Input-Border-Focused);
          box-shadow: var(--Input-Focus-Ring);
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div style={styles.container}>
      <label style={styles.label}>
        {label}
        {required && <span style={styles.requiredStar}>*</span>}
        {tooltipText && (
          <Tooltip content={tooltipText}>
            <Info style={styles.infoIcon} />
          </Tooltip>
        )}
      </label>
      {children}
      {helperText && !error && (
        <p style={styles.helperText}>{helperText}</p>
      )}
      {error && (
        <p style={styles.errorText}>
          <AlertCircle style={styles.errorIcon} />
          {error}
        </p>
      )}
    </div>
  );
};

export default FormField;